<template>
  <div class="pp-cols" data-test="pp-tab-financials">
    <div class="pp-main">
      <PanelCard title="Balance" icon="fa-dollar" test-id="pp-balance">
        <p class="pp-big" :class="{ 'pp-big--bad': estBalance > 0 }">{{ money(estBalance) }}</p>
        <p class="pp-muted">Open Dental's estimated balance for this patient, after expected insurance.</p>
        <dl class="pp-kv pp-kv--spaced pp-kv--left">
          <dt>Patient portion</dt><dd>{{ money(patientAmount) }}</dd>
          <template v-if="family">
            <dt>Family balance</dt>
            <dd class="pp-kv__bad" data-test="pp-family-balance">{{ money(family.amount) }}</dd>
          </template>
        </dl>
        <p v-if="family" class="pp-muted">{{ familySentence }}</p>
      </PanelCard>

      <p class="pp-footnote">
        The full ledger — charges, payments and adjustments — is in Open Dental. It is not shown here
        yet.
      </p>

      <!-- The Patient Profile page adds its full insurance panel here. -->
      <slot name="extra"></slot>
    </div>

    <div class="pp-side">
      <PanelCard v-if="claim" title="Unpaid claims" icon="fa-file-text-o" test-id="pp-claims">
        <dl class="pp-kv">
          <dt>Claims</dt><dd>{{ claim.claims || '—' }}</dd>
          <dt v-if="claim.oldestDateSent">Oldest sent</dt>
          <dd v-if="claim.oldestDateSent">{{ date(claim.oldestDateSent) }}</dd>
          <dt v-if="claim.daysOutstanding">Outstanding</dt>
          <dd v-if="claim.daysOutstanding">{{ claim.daysOutstanding }} days</dd>
          <dt v-if="claim.amount">Amount</dt><dd v-if="claim.amount">{{ money(claim.amount) }}</dd>
        </dl>
      </PanelCard>
    </div>
  </div>
</template>

<script>
/**
 * Financials — the balances Teamio already loads, and the balance / claim alerts spelled out.
 *
 * ⚠️ Three different numbers, labelled so nobody mistakes one for another:
 *   • EstBalance    — OD's estimated balance; what Patient Profile's header calls "Balance"
 *   • PatientAmount — BalTotal − InsEst, the patient portion the payment flow collects
 *   • family balance — the `balance` alert: the GUARANTOR's account, shared by the family
 * No ledger: Patient Profile has none either (Phase 7 decision, 2026-09-25).
 *
 * ⛔ No prop named `patNum`, `siteID` or `loading` — mixin.js computeds silently replace them.
 */
import PanelCard from '../PanelCard.vue';
import { money, formatOdDate } from '@/utils/patientPanel/format';
import { describeAlert } from '@/utils/scheduleView/alerts';

export default {
  name: 'FinancialsTab',

  components: { PanelCard },

  props: {
    payload: { type: Object, default: () => ({}) },
    alertDetails: { type: Object, default: () => ({}) },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
  },

  computed: {
    patient() {
      return (this.payload && this.payload.patient) || {};
    },
    estBalance() {
      const n = Number(this.patient.EstBalance);
      return Number.isFinite(n) ? n : 0;
    },
    patientAmount() {
      const n = Number(this.patient.PatientAmount);
      return Number.isFinite(n) ? n : 0;
    },
    family() {
      return (this.alertDetails && this.alertDetails.balance) || null;
    },
    familySentence() {
      return this.family ? describeAlert('balance', this.family) : '';
    },
    claim() {
      return (this.alertDetails && this.alertDetails.claim) || null;
    },
  },

  methods: {
    money,
    date(v) {
      return formatOdDate(v, this.dateFormat) || v;
    },
  },
};
</script>
